import { useMemo, type ReactElement } from 'react';
import { EvmReport } from '../components/cost';
import { buildEvmReportData } from '../integration/adapters';
import { hasBaseline } from '../integration/derivations';
import { useProjectStore } from '../store/project-store';
import { EmptyState } from './EmptyState';

/**
 * EVM 報表視圖。
 *
 * 沒有基準線時**不顯示滿版 N/A**：EVM 的每一個數字都以基準線為分母，沒有基準線
 * 時那些 0 是誤導的（看起來像「成本為零」而不是「無法計算」）。這裡直接換成
 * 引導語 + 一鍵設定基準線。`EvmReport` 本身也會在收到 `EVM_NO_BASELINE` 診斷時
 * 顯示同類引導，兩層都擋住，任一層先觸發都不會漏。
 */
export function EvmReportView({ readOnly = false }: { readonly readOnly?: boolean }): ReactElement {
  const project = useProjectStore((state) => state.project);
  const openBaselineManager = useProjectStore((state) => state.openBaselineManager);

  const data = useMemo(() => buildEvmReportData(project), [project]);
  const baselineReady = hasBaseline(project);

  if (!baselineReady) {
    return (
      <EmptyState
        testId="evm-no-baseline"
        title="尚未設定基準線"
        description="實獲值分析（EVM）需要一份基準線作為計畫值的來源。先設定基準線，之後回報進度時這裡就會顯示 PV / EV / AC 與 SPI / CPI。"
        action={readOnly ? null : (
          <button
            type="button"
            onClick={openBaselineManager}
            className="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500"
          >
            管理基準線
          </button>
        )}
      />
    );
  }

  return (
    <div data-testid="evm-report-view" className="h-full w-full overflow-auto rounded border border-slate-200 dark:border-slate-700">
      <EvmReport
        metrics={data.metrics}
        currency={project.currency}
        statusDate={data.statusDate}
        curve={data.curve}
        eacComparison={data.eacComparison}
        diagnostics={data.diagnostics}
      />
    </div>
  );
}
